import { useMemo } from "react";
import { X, Sparkles, CheckCircle2 } from "lucide-react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
  START_HOUR, END_HOUR, SLOT_MIN, SLOT_PX, TOTAL_SLOTS, SLOTS,
  DAY_LABELS_SHORT,
  ymd, slotInRange, slotInCfgRange, computeOverlapLayout, useNow,
} from "./utils";

function TimelineHeader({ children }) {
  return (
    <div className="week-head" style={{ padding: "10px 6px" }}>
      {children}
    </div>
  );
}

function NowLine({ dateStr, showDot = true }) {
  const now = useNow();
  const isToday = ymd(now) === dateStr;
  if (!isToday) return null;
  const minutes = now.getHours() * 60 + now.getMinutes();
  const startMin = START_HOUR * 60;
  const endMin = END_HOUR * 60;
  if (minutes < startMin || minutes > endMin) return null;
  const top = ((minutes - startMin) / SLOT_MIN) * SLOT_PX;
  return (
    <div className="absolute left-0 right-0 pointer-events-none z-20" style={{ top }} data-testid="now-line">
      <div className="relative">
        {showDot && (
          <div className="absolute -left-1 -top-1 w-2.5 h-2.5 rounded-full" style={{ background: "#EF4444", boxShadow: "0 0 0 3px rgba(239,68,68,0.2)" }} />
        )}
        <div className="h-[2px]" style={{ background: "#EF4444" }} />
      </div>
    </div>
  );
}

function TimelineColumn({ dateStr, staffMember, items, openHours, chipColor, chipTitle, onEdit, onDelete, openForm, apptMinutes, segmentsFor, dayCfgForColumn = null, columnStaffId = null, showNowLine = true, showNowDot = true }) {
  const layout = useMemo(() => computeOverlapLayout(items), [items]);
  // Build flat list of visible segments for this column. When columnStaffId is
  // provided (Staff view), filter to segments assigned to that operator.
  const segments = useMemo(() => {
    if (!segmentsFor) return [];
    const out = [];
    for (const a of items) {
      for (const seg of segmentsFor(a)) {
        if (columnStaffId && seg.staff_id !== columnStaffId) continue;
        out.push(seg);
      }
    }
    return out;
  }, [items, segmentsFor, columnStaffId]);
  return (
    <div className="relative" style={{ height: TOTAL_SLOTS * SLOT_PX }}>
      {SLOTS.map((s) => {
        const salonOk = slotInRange(dateStr, s.hour, s.minute, openHours);
        // In Staff view use per-staff effective config (respects staff-level
        // overrides like ferie/turni straordinari). Falls back to salon-wide
        // config for the unified Day/Week views.
        const cfg = dayCfgForColumn ? dayCfgForColumn(dateStr, staffMember?.id) : null;
        const cfgOk = !cfg || (!cfg.closed && slotInCfgRange(cfg, s.hour, s.minute));
        const staffOk = !staffMember?.working_hours || slotInRange(dateStr, s.hour, s.minute, staffMember.working_hours);
        const available = cfg ? cfgOk : (salonOk && staffOk);
        return (
          <TimelineSlot key={s.index} slot={s} dateStr={dateStr} staffId={staffMember?.id} available={available} openForm={openForm} />
        );
      })}
      {showNowLine && <NowLine dateStr={dateStr} showDot={showNowDot} />}
      {segments.map((seg, i) => {
        const start = seg.start;
        const mins = start.getUTCHours() * 60 + start.getUTCMinutes();
        const top = ((mins - START_HOUR * 60) / SLOT_MIN) * SLOT_PX;
        const dur = Math.max(15, (seg.end - seg.start) / 60000);
        const height = (dur / SLOT_MIN) * SLOT_PX - 2;
        const info = layout.get(seg.appt.id) || { col: 0, cols: 1 };
        return (
          <TimelineChip key={`${seg.appt.id}-${seg.kind}-${i}`} seg={seg} top={top} height={height} col={info.col} cols={info.cols} chipColor={chipColor} chipTitle={chipTitle} onEdit={onEdit} onDelete={onDelete} />
        );
      })}
    </div>
  );
}

function TimelineSlot({ slot, dateStr, staffId, available, openForm }) {
  const dropId = `${dateStr}::${String(slot.hour).padStart(2, "0")}:${String(slot.minute).padStart(2, "0")}${staffId ? `::${staffId}` : ""}`;
  // Always allow drop; unavailable slots ask for confirmation on drop in parent
  const { setNodeRef, isOver } = useDroppable({ id: dropId });
  const borderTop = slot.minute === 0 ? "1px solid var(--sp-border)" : "1px dashed rgba(148,163,184,0.25)";
  return (
    <div
      ref={setNodeRef}
      onClick={() => {
        if (!available) {
          if (!window.confirm("Questo slot è fuori dagli orari lavorativi. Vuoi comunque creare un appuntamento qui?")) return;
        }
        const et = new Date(0); et.setUTCHours(slot.hour, slot.minute + 30, 0);
        openForm({
          start_date: dateStr,
          start_time: `${String(slot.hour).padStart(2, "0")}:${String(slot.minute).padStart(2, "0")}`,
          end_time: `${String(et.getUTCHours()).padStart(2, "0")}:${String(et.getUTCMinutes()).padStart(2, "0")}`,
          staff_id: staffId || "",
        });
      }}
      style={{
        height: SLOT_PX,
        borderTop,
        background: !available ? "repeating-linear-gradient(45deg, rgba(148,163,184,0.22) 0 6px, transparent 6px 12px)"
          : (isOver ? "rgba(6,182,212,0.10)" : "transparent"),
        cursor: "pointer",
      }}
    />
  );
}

function TimelineChip({ seg, top, height, col, cols, chipColor, chipTitle, onEdit, onDelete }) {
  const appt = seg.appt;
  const isBuffer = seg.kind === "buffer";
  const isFirstService = !isBuffer && seg.is_first_service;
  // Only the FIRST service segment is draggable — otherwise dnd would fire
  // multiple ids per appt. Buffer segments are static.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `${appt.id}::drag::${seg.kind}-${seg.seg_index}`, disabled: !isFirstService });
  const svcColor = seg.service_color || chipColor(appt);
  const t = chipTitle(appt);
  const hm = (d) => `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;

  const gutter = 4;
  const overlapNudge = cols > 1 ? 8 : 0;
  const widthPct = 100 / cols;
  const leftPct = widthPct * col;
  const isCompleted = appt.status === "completed";
  const isCancelled = appt.status === "cancelled";
  const isNoShow = appt.status === "no_show";
  const isMuted = isCompleted || isCancelled || isNoShow;

  // Buffer segment: neutral striped block tinted with the appointment's own
  // color (follows the header 'Staff/Servizio' colorBy toggle via chipColor()).
  if (isBuffer) {
    const tint = chipColor(appt) || "#94A3B8";
    const bufferStyle = {
      top, height,
      left: cols === 1 ? `${gutter}px` : `calc(${leftPct}% + ${col === 0 ? gutter : 2}px)`,
      width: cols === 1 ? `calc(100% - ${gutter * 2}px)` : `calc(${widthPct}% + ${overlapNudge}px - ${col === cols - 1 ? gutter + 2 : 2}px)`,
      background: `repeating-linear-gradient(45deg, ${tint}55 0 6px, ${tint}18 6px 12px)`,
      border: `1px dashed ${tint}88`,
      color: tint,
      overflow: "hidden",
      zIndex: 9,
      opacity: isMuted ? 0.5 : 1,
    };
    return (
      <div
        onClick={(e) => { e.stopPropagation(); onEdit(appt); }}
        data-testid={`appt-${appt.id}-buffer-${seg.seg_index}`}
        data-tip={`Pulizia · ${seg.buffer_min} min`}
        className="absolute rounded-md px-2 py-0.5 text-[10px] cursor-pointer flex items-center gap-1 leading-tight"
        style={bufferStyle}>
        <Sparkles className="h-2.5 w-2.5 flex-shrink-0" />
        <span className="font-bold truncate">Pulizia · {seg.buffer_min}min</span>
      </div>
    );
  }

  // Service segment: colored block. If multi-service, use per-service color.
  const style = {
    top, height,
    left: cols === 1 ? `${gutter}px` : `calc(${leftPct}% + ${col === 0 ? gutter : 2}px)`,
    width: cols === 1 ? `calc(100% - ${gutter * 2}px)` : `calc(${widthPct}% + ${overlapNudge}px - ${col === cols - 1 ? gutter + 2 : 2}px)`,
    background: isMuted
      ? `repeating-linear-gradient(135deg, ${svcColor}66 0 8px, ${svcColor}44 8px 16px)`
      : `linear-gradient(135deg, ${svcColor}ee 0%, ${svcColor}bb 100%)`,
    color: "#fff",
    boxShadow: `0 2px 8px ${svcColor}44, ${cols > 1 ? "0 0 0 1.5px #fff" : "none"}`,
    overflow: "hidden",
    zIndex: 10 + col,
    opacity: isMuted ? 0.88 : 1,
    border: isMuted ? `1.5px solid ${svcColor}` : undefined,
  };
  const statusBadge = isCompleted
    ? { label: "Chiuso", color: svcColor, textColor: svcColor, tone: "success" }
    : isCancelled
      ? { label: "Cancellato", color: "#DC2626", textColor: "#DC2626", tone: "danger" }
      : isNoShow
        ? { label: "Non presentato", color: "#DC2626", textColor: "#DC2626", tone: "danger" }
        : null;

  // Show service name when appointment has multiple service items
  const showServiceName = seg.service_name && (appt.service_items?.length || 0) > 1;

  return (
    <div ref={setNodeRef} {...attributes} {...listeners}
      onClick={(e) => { e.stopPropagation(); onEdit(appt); }}
      data-testid={`appt-${appt.id}${isFirstService ? "" : `-svc-${seg.seg_index}`}`}
      data-tip={`${t.primary}\n${showServiceName ? seg.service_name + "\n" : ""}${hm(seg.start)}–${hm(seg.end)}${statusBadge ? "\n" + statusBadge.label : ""}`}
      className={`absolute rounded-md px-2 py-1 text-xs ${isFirstService ? "cursor-grab" : "cursor-pointer"} ${isDragging ? "opacity-40" : ""}`}
      style={style}>
      {statusBadge && isFirstService && (
        <div className="absolute top-1 right-1 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/95 text-[9px] font-black uppercase tracking-wider" style={{ color: statusBadge.textColor }}>
          {isCompleted && <CheckCircle2 className="h-2.5 w-2.5" />}
          {statusBadge.label}
        </div>
      )}
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          <div className={`font-black truncate text-[11px] leading-tight flex items-center gap-1 ${isMuted ? "line-through decoration-2" : ""}`}>
            <span className="truncate">{t.primary}</span>
          </div>
          {showServiceName ? (
            <div className={`opacity-90 truncate text-[10px] leading-tight ${isMuted ? "line-through" : ""}`}>{seg.service_name}</div>
          ) : (
            <div className={`opacity-90 truncate text-[10px] leading-tight ${isMuted ? "line-through" : ""}`}>{t.secondary}</div>
          )}
          {height >= SLOT_PX * 2 && <div className="opacity-80 text-[10px] mt-0.5">{hm(seg.start)}–{hm(seg.end)}</div>}
        </div>
        {height >= SLOT_PX * 1.5 && onDelete && cols === 1 && !isMuted && isFirstService && (
          <button data-testid={`appt-delete-${appt.id}`} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onDelete(appt.id); }} className="opacity-70 hover:opacity-100">
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function HourAxis() {
  return (
    <div className="relative bg-[color:var(--sp-subtle)]" style={{ height: TOTAL_SLOTS * SLOT_PX }}>
      {SLOTS.filter((s) => s.minute === 0).map((s) => (
        <div key={s.index} className="text-[10px] font-black text-[color:var(--sp-text-secondary)] pr-2 text-right"
             style={{ position: "absolute", top: (s.index * SLOT_PX) - 6, right: 0, left: 0, height: 12 }}>
          {String(s.hour).padStart(2, "0")}:00
        </div>
      ))}
    </div>
  );
}

// ---------- Day Time ----------
export function TimelineDay({ date, items, openHours, chipColor, chipTitle, onEdit, onDelete, openForm, apptMinutes, segmentsFor }) {
  return (
    <div className="sp-card overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: "56px 1fr" }}>
        <div />
        <TimelineHeader>
          <div className="font-black text-[color:var(--sp-text)]">Tutti gli appuntamenti</div>
        </TimelineHeader>
        <HourAxis />
        <TimelineColumn dateStr={date} staffMember={null} items={items} openHours={openHours} chipColor={chipColor} chipTitle={chipTitle} onEdit={onEdit} onDelete={onDelete} openForm={openForm} apptMinutes={apptMinutes} segmentsFor={segmentsFor} />
      </div>
    </div>
  );
}

// ---------- Day Staff ----------
export function TimelineStaff({ date, staff, items, openHours, chipColor, chipTitle, onEdit, onDelete, openForm, apptMinutes, segmentsFor, dayCfgForColumn }) {
  if (staff.length === 0) return <div className="sp-card p-12 text-center text-[color:var(--sp-text-secondary)]">Aggiungi almeno un membro dello staff per usare questa vista.</div>;
  // Group items: an appt appears in every column its segments touch. This lets
  // multi-operator appointments show in each staff column WITHOUT duplicating
  // the underlying record (onClick still opens the same appt).
  const itemsByStaff = {};
  for (const a of items) {
    const staffIds = new Set();
    for (const seg of segmentsFor(a)) if (seg.staff_id) staffIds.add(seg.staff_id);
    if (!staffIds.size && a.staff_id) staffIds.add(a.staff_id);
    for (const sid of staffIds) (itemsByStaff[sid] = itemsByStaff[sid] || []).push(a);
  }
  return (
    <div className="sp-card overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: `56px repeat(${staff.length}, minmax(0, 1fr))` }}>
        <div />
        {staff.map((s) => {
          const cfg = dayCfgForColumn ? dayCfgForColumn(date, s.id) : null;
          const closedBadge = cfg?.closed
            ? (cfg._scope === "staff" ? { label: "FERIE", color: "#DC2626" } : { label: "CHIUSO", color: "#94A3B8" })
            : null;
          return (
            <TimelineHeader key={s.id}>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color || "#3B82F6" }} />
                <span className="font-black text-[color:var(--sp-text)]">{s.name}</span>
                {closedBadge && (
                  <span data-testid={`staff-col-badge-${s.id}`}
                        className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white"
                        style={{ background: closedBadge.color }}>
                    {closedBadge.label}
                  </span>
                )}
              </div>
            </TimelineHeader>
          );
        })}

        <HourAxis />
        {staff.map((s, i) => (
          <TimelineColumn key={s.id} dateStr={date} staffMember={s} items={itemsByStaff[s.id] || []} openHours={openHours} chipColor={chipColor} chipTitle={chipTitle} onEdit={onEdit} onDelete={onDelete} openForm={openForm} apptMinutes={apptMinutes} segmentsFor={segmentsFor} dayCfgForColumn={dayCfgForColumn} columnStaffId={s.id} showNowDot={i === 0} />
        ))}
      </div>
    </div>
  );
}

// ---------- Week (7 columns) ----------
export function TimelineWeek({ weekDays, items, openHours, chipColor, chipTitle, onEdit, openForm, apptMinutes, segmentsFor, today }) {
  const byDayMap = {};
  for (const a of items) { const k = ymd(new Date(a.start)); (byDayMap[k] = byDayMap[k] || []).push(a); }
  return (
    <div className="sp-card overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: `56px repeat(7, minmax(0, 1fr))` }}>
        <div />
        {weekDays.map((d, i) => (
          <TimelineHeader key={d}>
            <div className={`text-center ${d === today ? "text-[color:var(--sp-brand)]" : "text-[color:var(--sp-text-secondary)]"}`}>
              <div className="text-[10px] uppercase tracking-widest">{DAY_LABELS_SHORT[i]}</div>
              <div className="text-sm font-black text-[color:var(--sp-text)]">{d.slice(8, 10)}</div>
            </div>
          </TimelineHeader>
        ))}

        <HourAxis />
        {weekDays.map((d, i) => (
          <TimelineColumn key={d} dateStr={d} staffMember={null} items={byDayMap[d] || []} openHours={openHours} chipColor={chipColor} chipTitle={chipTitle} onEdit={onEdit} onDelete={null} openForm={openForm} apptMinutes={apptMinutes} segmentsFor={segmentsFor} />
        ))}
      </div>
    </div>
  );
}
